import { useState, useEffect, useMemo, useCallback } from 'react';
import { Drawer, Form, Input, Select, Button, Space, TreeSelect, Row, Col, Table, Typography, Alert, Tooltip, Spin, Modal, Image, InputNumber, Radio, Upload, message } from 'antd';
import { laborFeeCostBearers, expertLevels, laborFeePaymentMethods, CurrencyList } from '@utils/constants';
import { TABLE_PAGE_SIZE_OPTIONS, filterFormGridLayout } from '@utils/property';
import { convertDateTimeToLocalText, formatOfficeAndPost, buildAuthDictMaps, prepareBCOSSUrl, numberProject } from '@utils/utils';
import { filterTreeNodeByTitle } from '@/utils/antdTreeSelect';
import SubmitAuditDrawer from './SubmitAuditDrawer';
import ExpertInfoCard from '@/components/common/ExpertInfoCard';
import GuestTaskList from '@/components/common/GuestTaskList';
import './PaymentBatchDrawer.less';
import config from '@/config';
import { getAdminInfo, dateTimeFormat } from '@utils/utils';
import { localStorageGet } from '@core/cookie';
import dayjs from 'dayjs';
import { fetchLaborFeePaymentBatchesList, laborFeeListApi, batchStatisticalAmountApi, calculateIndividualTaxApi, updateLaborFeeRecordsApi, laborFeeBatchInvalidApi } from '@services/SereviseConst';
import { getConferenceListApi } from '@services/Agenda';
import { fileApi } from '@services/Conference';
import { authdictionaryApi } from '@services/Conference/sign';
import { isEmpty } from 'lodash';

const { Text } = Typography;

const filterFormItemLayout = {
    labelCol: {
        sm: { span: 20 },
        md: { span: 8 },
    },
    wrapperCol: {
        sm: { span: 24 },
        md: { span: 16 },
    },
};

const PaymentBatchDrawer = ({
    isOpen,
    onClose,
    conferenceId,
    conference,
    statisticalAmount,
    getAmountStat,
    selectedBatchNo: propsSelectedBatchNo, // 来自父组件传来的批次号，需要自动选中
    onInvalidSuccess, // 作废成功后的父级回调，用于刷新父页面批次列表状态
    onApprovalFlowComplete, // 从外部页面进入时，审批完成后关闭批次抽屉并返回来源页
}) => {
    const [batchList, setBatchList] = useState([]);
    const [selectedBatchNo, setSelectedBatchNo] = useState(null);
    const [filterForm] = Form.useForm();
    const [conferenceHallTreeData, setConferenceHallTreeData] = useState([]);
    const [submitAuditDrawerOpen, setSubmitAuditDrawerOpen] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [authDict, setAuthDict] = useState([]); // 认证字典数据
    const [editModalOpen, setEditModalOpen] = useState(false); // 编辑弹框状态
    const [selectedRecord, setSelectedRecord] = useState(null); // 选中的记录
    const [editingRecord, setEditingRecord] = useState(null); // 正在编辑的记录
    const [previewImage, setPreviewImage] = useState(null); // 图片预览
    const [tableData, setTableData] = useState([]); // 表格数据
    const [total, setTotal] = useState(0); // 总数
    const [filter, setFilter] = useState({ page: 1, pageSize: 10 }); // 筛选条件和分页
    const [batchStatisticalAmount, setBatchStatisticalAmount] = useState(null); // 批次统计信息
    const [editForm] = Form.useForm(); // 编辑表单实例
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(1); // 选中的付款方式
    const [selectedCurrency, setSelectedCurrency] = useState('CNY'); // 选中的货币
    const [conferencePhotoUrl, setConferencePhotoUrl] = useState(''); // 专家参与情况图片盖章版URL
    const [certificateFileUrl, setCertificateFileUrl] = useState(''); // 专家劳务确认函盖章版URL
    const [conferencePhotoUploadLoading, setConferencePhotoUploadLoading] = useState(false); // 专家参与情况图片上传加载状态
    const [certificateFileUploadLoading, setCertificateFileUploadLoading] = useState(false); // 专家劳务确认函上传加载状态
    const [taxCalculating, setTaxCalculating] = useState(false); // 税费计算中
    const [invalidLoading, setInvalidLoading] = useState(false); // 作废按钮loading

    // 根据认证字典构建映射
    const dictMaps = useMemo(() => buildAuthDictMaps(authDict), [authDict]);

    // 请求认证字典数据
    const requestAuthDict = async () => {
        const res = await authdictionaryApi();
        if (!isEmpty(res?.options)) {
            const authDictData = res.options;
            authDictData
                && Array.isArray(authDictData)
                && authDictData.forEach(authType => {
                    authType.labels
                        && Array.isArray(authType.labels)
                        && authType.labels.forEach(label => {
                            label.options
                                && Array.isArray(label.options)
                                && label.options.forEach(l => {
                                    if (isEmpty(l.options)) {
                                        delete l.options;
                                    }
                                });
                        });
                });
            setAuthDict(authDictData);
        }
    };

    useEffect(() => {
        requestAuthDict();
    }, []);

    // 转换会议厅树数据
    const prepareConferenceHallTreeData = useCallback(data => {
        const treeData = [];
        if (Object.keys(data)?.length) {
            Object.keys(data).forEach(agendaDate => {
                const dateNode = {
                    title: agendaDate,
                    value: agendaDate,
                    selectable: false,
                    hallNum: 0,
                    children: [],
                };
                const confHalls = data[agendaDate];
                confHalls?.length
                    && confHalls.forEach(confHall => {
                        const hallNode = {
                            title: `${confHall.name} [YHYC#${confHall.id}]`,
                            value: confHall.id,
                            children: [],
                        };
                        const confSubHalls = confHall.secondHallsList || [];
                        if (confSubHalls?.length) {
                            hallNode.selectable = false;
                            confSubHalls.forEach(subHall => {
                                dateNode.hallNum += 1;
                                hallNode.children.push({
                                    title: `${subHall.name} [YHYC#${subHall.id}]`,
                                    value: subHall.id,
                                });
                            });
                        } else {
                            dateNode.hallNum += 1;
                        }
                        dateNode.children.push(hallNode);
                    });
                treeData.push(dateNode);
            });
        }
        return treeData;
    }, []);

    // 获取会议列表（用于"任务所属会议"筛选项）
    const getConferenceList = useCallback(async () => {
        try {
            const res = await getConferenceListApi({ conferenceId });
            if (res?.status_code === 200 && res?.data) {
                const agendaData = res.data;
                setConferenceHallTreeData(prepareConferenceHallTreeData(agendaData));
            }
        } catch (error) {
            console.error('获取会议列表失败:', error);
        }
    }, [conferenceId, prepareConferenceHallTreeData]);

    // 请求批次统计信息
    const getBatchStatisticalAmount = useCallback(
        async batchNo => {
            if (!batchNo || !selectedBatchNo) {
                return;
            }
            try {
                const res = await batchStatisticalAmountApi({
                    batchNo,
                    projectId: conferenceId,
                    projectCode: 'YHY',
                });
                if (res?.status_code === 200 && res?.data) {
                    setBatchStatisticalAmount(res.data);
                }
            } catch (error) {
                console.error('获取批次统计信息失败:', error);
            }
        },
        [selectedBatchNo, conferenceId],
    );

    // 初始化时获取会议列表
    useEffect(() => {
        if (isOpen && conferenceId) {
            getConferenceList();
        }
    }, [isOpen, conferenceId, getConferenceList]);

    // 当接收到来自父组件的 selectedBatchNo 时，自动选中对应的批次
    useEffect(() => {
        if (propsSelectedBatchNo && batchList.length > 0) {
            // 验证该批次是否在列表中存在
            const matchedBatch = batchList.find(batch => batch.batchNo === propsSelectedBatchNo);
            if (matchedBatch) {
                setSelectedBatchNo(propsSelectedBatchNo);
                setFilter(prev => ({ ...prev, page: 1, pageSize: 10, batchNo: propsSelectedBatchNo }));
                // 请求批次统计信息
                getBatchStatisticalAmount(propsSelectedBatchNo);
            }
        }
    }, [propsSelectedBatchNo, batchList, conferenceId]);

    // 如果地址栏没有批次号，则默认选中第一个批次
    useEffect(() => {
        if (!propsSelectedBatchNo && batchList.length > 0 && !selectedBatchNo) {
            const firstBatch = batchList[0];
            setSelectedBatchNo(firstBatch.batchNo);
            setFilter(prev => ({ ...prev, page: 1, pageSize: 10, batchNo: firstBatch.batchNo }));
            // 请求批次统计信息
            getBatchStatisticalAmount(firstBatch.batchNo);
        }
    }, [batchList, propsSelectedBatchNo, selectedBatchNo, conferenceId]);

    // 打开编辑弹框
    const handleEditRecord = (row, record) => {
        setSelectedRecord(row);
        setEditingRecord(record); // 保存正在编辑的记录
        setSelectedPaymentMethod(record.paymentMethod || 1);
        setSelectedCurrency(record.currency || 'CNY');
        setConferencePhotoUrl(record.expertParticipationPic || '');
        setCertificateFileUrl(record.expertLaborFeeConfirmPic || '');

        // 先设置字段值，再打开弹框
        editForm.setFieldsValue({
            paymentMethod: record.paymentMethod || 1,
            currency: record.currency || 'CNY',
            costBearer: record.costBearer,
            laborFeeAmount: record.laborFeeAmount,
            foreignExchangeAmount: record.foreignExchangeAmount,
            expertParticipationPic: record.expertParticipationPic,
            expertLaborFeeConfirmPic: record.expertLaborFeeConfirmPic,
        });
        setEditModalOpen(true);
    };

    // 关闭编辑弹框
    const handleCloseEditModal = () => {
        setEditModalOpen(false);
        setSelectedRecord(null);
        setEditingRecord(null); // 清空正在编辑的记录
        setSelectedPaymentMethod(1);
        setSelectedCurrency('CNY');
        setConferencePhotoUrl('');
        setCertificateFileUrl('');
        editForm.resetFields();
    };

    // 检查图片上传文件
    const checkImgUploadFile = (file, sizeMb) => {
        const typeList = ['image/jpg', 'image/jpeg', 'image/png'];
        const isImage = typeList.includes(file.type) || false;

        if (!isImage) {
            message.error('仅支持 JPG/PNG 图片格式');
            return false;
        }

        const metSize = file.size / 1024 / 1024 < sizeMb;
        if (!metSize) {
            message.error(`图片文件大小限${sizeMb}MB`);
            return false;
        }

        return isImage && metSize;
    };

    // 上传图片文件
    const uploadImageFile = (fileInfo, type) => {
        const formData = new FormData();
        formData.append('file', fileInfo.file);
        formData.append('type', 14);

        // 根据上传类型设置相应的loading状态
        if (type === 'expertParticipationPic') {
            setConferencePhotoUploadLoading(true);
        } else if (type === 'expertLaborFeeConfirmPic') {
            setCertificateFileUploadLoading(true);
        }

        fileApi(formData, { headers: { 'Content-Type': 'multipart/form-data' } })
            .then(res => {
                // 根据上传类型设置相应的loading状态
                if (type === 'expertParticipationPic') {
                    setConferencePhotoUploadLoading(false);
                } else if (type === 'expertLaborFeeConfirmPic') {
                    setCertificateFileUploadLoading(false);
                }

                if (!res) {
                    message.error('文件上传失败');
                    return false;
                }

                if (res.status_code === 200) {
                    if (type === 'expertParticipationPic') {
                        setConferencePhotoUrl(res.data);
                        editForm.setFieldsValue({ expertParticipationPic: res.data });
                        // 上传完成后清除该字段的校验错误
                        editForm.setFields([
                            {
                                name: 'expertParticipationPic',
                                errors: null,
                            },
                        ]);
                        message.success('专家参与情况图片上传成功');
                    } else if (type === 'expertLaborFeeConfirmPic') {
                        setCertificateFileUrl(res.data);
                        editForm.setFieldsValue({ expertLaborFeeConfirmPic: res.data });
                        // 上传完成后清除该字段的校验错误
                        editForm.setFields([
                            {
                                name: 'expertLaborFeeConfirmPic',
                                errors: null,
                            },
                        ]);
                        message.success('专家劳务确认函上传成功');
                    }
                }
            })
            .catch(error => {
                // 根据上传类型设置相应的loading状态
                if (type === 'expertParticipationPic') {
                    setConferencePhotoUploadLoading(false);
                } else if (type === 'expertLaborFeeConfirmPic') {
                    setCertificateFileUploadLoading(false);
                }
                console.error('上传失败:', error);
            });
    };

    // 计算外币折合人民币税费
    const handleForeignAmountBlur = async e => {
        const foreignExchangeAmount = e.target.value;
        if (!foreignExchangeAmount || isNaN(foreignExchangeAmount)) {
            return;
        }

        const formValues = editForm.getFieldsValue();
        const paymentMethod = formValues.paymentMethod || 1;

        try {
            setTaxCalculating(true);
            const res = await calculateIndividualTaxApi({
                paymentMethod,
                money: parseFloat(foreignExchangeAmount),
                from: selectedCurrency,
                to: 'CNY',
            });

            if (res && res.status_code === 200 && res.data) {
                // 更新编辑表单数据中的折合人民币金额
                // 接口返回：money（实发劳务费），individualTax（个人所得税）
                const cnyCurrencyLaborFeeAmount = parseFloat(res.data.money) || 0;
                const cnyCurrencyTaxAmount = parseFloat(res.data.individualTax) || 0;
                const cnyCurrencyTotalAmount = cnyCurrencyLaborFeeAmount + cnyCurrencyTaxAmount;

                if (formValues.currency === 'CNY') {
                    setEditingRecord(prev => ({
                        ...prev,
                        individualTaxAmount: cnyCurrencyTaxAmount,
                        laborFeeAmount: foreignExchangeAmount,
                    }));
                } else {
                    setEditingRecord(prev => ({
                        ...prev,
                        cnyCurrencyLaborFeeAmount,
                        cnyCurrencyTaxAmount,
                        cnyCurrencyTotalAmount,
                    }));
                }
            } else {
                message.error('折算失败');
            }
        } catch (error) {
            console.error('折算失败:', error);
            message.error('折算失败，请重试');
        } finally {
            setTaxCalculating(false);
        }
    };

    // 提交编辑表单
    const handleSubmitEditForm = async () => {
        try {
            await editForm.validateFields();
            const formValues = editForm.getFieldsValue();

            const { expertParticipationPic, expertLaborFeeConfirmPic, paymentMethod } = formValues;

            if (paymentMethod !== 3 && !expertParticipationPic && formValues.currency !== 'CNY') {
                editForm.setFields([
                    {
                        name: 'expertParticipationPic',
                        errors: ['请上传专家参与情况图片'],
                    },
                ]);
            }
            if (paymentMethod !== 3 && !expertLaborFeeConfirmPic && formValues.currency !== 'CNY') {
                editForm.setFields([
                    {
                        name: 'expertLaborFeeConfirmPic',
                        errors: ['请上传专家劳务确认函'],
                    },
                ]);
            }
            if (paymentMethod !== 3 && (!expertParticipationPic || !expertLaborFeeConfirmPic) && formValues.currency !== 'CNY') {
                return;
            }

            // 从表单获取的 formValues 包含所有字段值
            const submitData = {
                id: selectedRecord?.id,
                batchNo: selectedBatchNo,
                expertId: selectedRecord?.expertId,
                projectId: conferenceId,
                projectName: conference?.name,
                paymentMethod: formValues.paymentMethod || 1,
                currency: formValues.currency || 'CNY',
                expertParticipationPic,
                expertLaborFeeConfirmPic,
                costBearer: formValues.costBearer,
            };

            // 根据币种添加对应的金额数据
            if (formValues.currency === 'CNY') {
                // 人民币场景
                submitData.laborFeeAmount = formValues.laborFeeAmount;
            } else {
                // 外币场景
                submitData.foreignExchangeAmount = formValues.foreignExchangeAmount;
                submitData.costBearer = 5;
            }

            console.log('完整表单数据:', submitData);

            // 调用 API 保存劳务费信息
            try {
                const res = await updateLaborFeeRecordsApi(submitData);
                if (res && res.status_code === 200) {
                    message.success('劳务费信息已更新');
                    handleCloseEditModal();
                    // 刷新表格数据
                    fetchTableData();
                    // 刷新付款申请统计信息和本项目累计付款申请
                    getBatchStatisticalAmount(selectedBatchNo);
                    getAmountStat();
                }
            } catch (error) {
                console.error('API 调用失败:', error);
            }
        } catch (error) {
            console.error('表单提交失败:', error);
        }
    };

    // 获取劳务费列表数据
    const fetchTableData = async () => {
        try {
            setExportLoading(true);
            // 构建请求参数，包含基础参数和筛选条件
            const params = {
                projectCode: 'YHY',
                projectId: conferenceId,
                ...filter,
            };
            const res = await laborFeeListApi(params);
            if (res && res.status_code === 200) {
                setTableData(res.data?.list || []);
                setTotal(res.data?.totalCount || 0);
            }
        } catch (error) {
            console.error('获取劳务费列表失败:', error);
        } finally {
            setExportLoading(false);
        }
    };

    // 根据idcardType获取证件类型
    const getIdCardType = cardType => {
        const type = parseInt(cardType, 10);
        const cardTypes = [
            { code: 1, name: '身份证' },
            { code: 2, name: '港澳台来往内地通行证' },
            { code: 3, name: '台湾居民来往大陆通行证' },
            { code: 4, name: '外国人永久居留身份证' },
            { code: 5, name: '护照' },
        ];
        const target = cardTypes.find(item => item.code === type);
        return target?.name || null;
    };

    // 展开/收起实际任务
    const toggleAgendaTasks = guestAgendaInfo => {
        setTableData(
            tableData.map(item => {
                if (item.id === guestAgendaInfo.id) {
                    return { ...item, expanded: !item.expanded };
                } else {
                    return item;
                }
            }),
        );
    };

    // 付款批次表格的独立列配置（去掉代确认、上传凭证等功能）
    const batchDefaultColumns = [
        {
            title: '序号',
            dataIndex: 'rowIndex',
            width: 80,
            align: 'center',
            fixed: 'left',
            render: (_, __, index) => index + 1,
        },
        {
            title: '基本信息',
            width: 200,
            dataIndex: 'memUsername',
            fixed: 'left',
            render: (_, record) => <ExpertInfoCard expert={record} />,
        },
        {
            title: '单位及职务',
            dataIndex: 'unitPost',
            width: 180,
            render: (_, row) => {
                if (row.workExpList?.length) {
                    return (
                        <>
                            {row.workExpList.map((item, index) => {
                                const officePostDisplay = formatOfficeAndPost(item, dictMaps);
                                const unitName = item.unitName || '';
                                const fullDisplay = [unitName, officePostDisplay].filter(Boolean).join('');
                                const displayIndex = index + 1;

                                return (
                                    <span key={`w_${item.id}`}>
                                        {row.workExpList.length > 1 && `${displayIndex}. `}
                                        {fullDisplay || ''}
                                    </span>
                                );
                            })}
                        </>
                    );
                } else {
                    return '—';
                }
            },
        },
        {
            title: '实际任务',
            dataIndex: 'agendasList',
            width: 400,
            render: (_, record) => {
                const agendasGuestsList = record.agendasList || [];
                return (
                    <>
                        <GuestTaskList agendasGuestsList={agendasGuestsList} expanded={record.expanded} onToggleExpansion={toggleAgendaTasks} record={record} />
                        <div>{record.agendaConfirmed ? <Text type="success">已确认</Text> : <Text type="secondary">未反馈</Text>}</div>
                    </>
                );
            },
        },
        {
            title: '本次申请劳务费',
            dataIndex: 'createdAt',
            width: 280,
            render: (_, row) => {
                const laborFeePayRecordDtoList = row.laborFeePayRecordDtoList || [];
                const record = laborFeePayRecordDtoList.find(r => r.batchNo === selectedBatchNo) || {};

                // 判断是否为外币（非CNY）
                const isForeignCurrency = record.currency && record.currency !== 'CNY';
                const paymentMethodLabel = laborFeePaymentMethods.find(m => m.value === record.paymentMethod)?.label || <Text type="secondary">暂无</Text>;
                const costBearerLabel = laborFeeCostBearers.find(m => m.value === record.costBearer)?.label || <Text type="secondary">暂无</Text>;

                // 共用的顶部内容（付款方式、支付主体、编辑按钮）
                const commonContent = (
                    <>
                        <div>付款方式：{paymentMethodLabel}</div>
                        <div>支付主体：{costBearerLabel}</div>
                    </>
                );

                // 折合人民币含税劳务费
                const totalAmount = parseFloat(record.laborFeeAmount) + parseFloat(record.individualTaxAmount);

                // 币种名称
                const currencyName = () => CurrencyList.find(c => c.currency === record.currency)?.name;

                // 币种特定的金额内容
                const currencyContent = isForeignCurrency ? (
                    // 外币（非CNY）
                    <>
                        <div>支付币种：{currencyName ? currencyName() : <Text type="secondary">暂无</Text>}</div>
                        <div>
                            外币实发额({currencyName() || <Text type="secondary">暂无</Text>})：{record.foreignExchangeAmount ? formatCurrency(record.foreignExchangeAmount) : <Text type="secondary">暂无</Text>}
                        </div>
                        <div>折合人民币实发劳务费(元)：{record.laborFeeAmount ? formatCurrency(record.laborFeeAmount) : <Text type="secondary">暂无</Text>}</div>
                        <div>折合人民币个人所得税(元)：{record.individualTaxAmount ? formatCurrency(record.individualTaxAmount) : <Text type="secondary">暂无</Text>}</div>
                        <div>折合人民币含税劳务费(元)：{totalAmount ? formatCurrency(totalAmount) : <Text type="secondary">暂无</Text>}</div>
                        <div>
                            专家参与情况图片盖章版：
                            {record.expertParticipationPic ? (
                                <Button type="link" size="small" style={{ paddingLeft: 0 }} onClick={() => setPreviewImage(record.expertParticipationPic)}>
                                    查看
                                </Button>
                            ) : (
                                <Text type="secondary">暂无</Text>
                            )}
                        </div>
                        <div>
                            专家劳务确认函盖章版：
                            {record.expertLaborFeeConfirmPic ? (
                                <Button type="link" size="small" style={{ paddingLeft: 0 }} onClick={() => setPreviewImage(record.expertLaborFeeConfirmPic)}>
                                    查看
                                </Button>
                            ) : (
                                <Text type="secondary">暂无</Text>
                            )}
                        </div>
                    </>
                ) : (
                    // 人民币（CNY）
                    <>
                        <div>实发劳务费(元)：{record.laborFeeAmount ? formatCurrency(record.laborFeeAmount) : <Text type="secondary">暂无</Text>}</div>
                        <div>个人所得税(元)：{record.individualTaxAmount ? formatCurrency(record.individualTaxAmount) : <Text type="secondary">暂无</Text>}</div>
                        <div>含税劳务费(元)：{record.laborFeeAmount && record.individualTaxAmount ? formatCurrency(parseFloat(record.laborFeeAmount) + parseFloat(record.individualTaxAmount)) : <Text type="secondary">暂无</Text>}</div>
                    </>
                );

                const recordCopy = {
                    ...record,
                    cnyCurrencyLaborFeeAmount: record.laborFeeAmount,
                    cnyCurrencyTaxAmount: record.individualTaxAmount,
                    cnyCurrencyTotalAmount: totalAmount,
                };

                // 共用的编辑按钮，如果当前申请批次已提交，则不可编辑
                const editButton =
                    !record.payedAt && (record.approvalStatus === 1 || !record.approvalStatus) ? (
                        <div>
                            <Button
                                type="link"
                                size="small"
                                style={{ paddingLeft: 8, paddingRight: 0, display: 'flex', alignItems: 'center' }}
                                icon={
                                    <span className="anticon">
                                        <i className="bi bi-pencil"></i>
                                    </span>
                                }
                                onClick={() => handleEditRecord(row, recordCopy)}
                            >
                                编辑
                            </Button>
                        </div>
                    ) : null;

                return (
                    <>
                        {commonContent}
                        {currencyContent}
                        {editButton}
                    </>
                );
            },
        },
        {
            title: '其他批次付款申请记录',
            dataIndex: 'laborFeePayRecordDtoList',
            width: 300,
            render: (_, record) => {
                const payRecords = record.laborFeePayRecordDtoList || [];
                // 过滤掉当前选中的批次
                const filteredPayRecords = payRecords.filter(payRecord => payRecord.batchNo !== selectedBatchNo);

                if (!filteredPayRecords || filteredPayRecords.length === 0) {
                    return <Text type="secondary">暂无</Text>;
                }

                // 按卡片格式展示所有付款记录
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {filteredPayRecords.map(payRecord => {
                            const costBearerLabel = laborFeeCostBearers.find(b => b.value === payRecord.costBearer)?.label || <Text type="secondary">暂无</Text>;
                            // 判断是否为外币
                            const isForeignCurrency = payRecord.currency && payRecord.currency !== 'CNY';
                            const currencyType = CurrencyList.find(c => c.currency === payRecord.currency)?.name || payRecord.currency;

                            return (
                                <div key={payRecord.batchNo} className="pay-record-card">
                                    <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
                                        <div>
                                            申请批次：{convertDateTimeToLocalText(payRecord.createdAt)}
                                            {payRecord.createdName && `(${payRecord.createdName})`}
                                        </div>
                                        <div>支付主体：{costBearerLabel}</div>
                                        {isForeignCurrency ? (
                                            // 外币展示格式
                                            <>
                                                <div>
                                                    外币实发额({currencyType})：{payRecord.foreignExchangeAmount !== null ? formatCurrency(payRecord.foreignExchangeAmount) : <Text type="secondary">暂无</Text>}(
                                                    {payRecord.approvalStatus === 1 || !payRecord.approvalStatus ? <Text type="warning">待提交</Text> : payRecord.payedAt ? <Text type="success">已付款</Text> : <Text type="warning">待付款</Text>})
                                                </div>
                                                <div>折合人民币实发劳务费(元)：{payRecord.laborFeeAmount !== null ? `${formatCurrency(payRecord.laborFeeAmount)}` : <Text type="secondary">暂无</Text>}</div>
                                            </>
                                        ) : (
                                            // 人民币展示格式
                                            <div>
                                                实发劳务费(元)：{payRecord.laborFeeAmount !== null ? `${formatCurrency(payRecord.laborFeeAmount)}` : <Text type="secondary">暂无</Text>}(
                                                {payRecord.approvalStatus === 1 || !payRecord.approvalStatus ? <Text type="warning">待提交</Text> : payRecord.payedAt ? <Text type="success">已付款</Text> : <Text type="warning">待付款</Text>})
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );
            },
        },
        {
            title: '收款信息',
            dataIndex: 'bankName',
            width: 250,
            render: (_, record) => (
                <>
                    {(record.idcardType === 1 || !record.idcardType) && (
                        <>
                            <span>身份证号码：{record.idCard || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>银行账号：{record.bankCardNo || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>开户银行：{record.bankName || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <div>{record.laborInfoConfirmed ? <Text type="success">已确认</Text> : <Text type="secondary">未反馈</Text>}</div>
                        </>
                    )}
                    {record.idcardType > 1 && record.bankType !== 2 && (
                        <>
                            <span>证件类型：{getIdCardType(record.idcardType) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>证件号：{record.idCard || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>国籍：{record.nationality || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>出生日期：{(!!record.birthday && dayjs(record.birthday).format('YYYY-MM-DD')) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>出生地：{record.birthPlace || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>性别：{(record.memGender !== null && (Number(record.memGender) === 1 ? '男' : '女')) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>银行账号：{record.bankCardNo || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>开户银行：{record.bankName || <Text type="secondary">&ndash;</Text>}</span>
                            <div>{record.laborInfoConfirmed ? <Text type="success">已确认</Text> : <Text type="secondary">未反馈</Text>}</div>
                        </>
                    )}
                    {record.idcardType > 1 && record.bankType === 2 && (
                        <>
                            <span>证件类型：{getIdCardType(record.idcardType) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>证件号：{record.idCard || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>国籍：{record.nationality || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>出生日期：{(!!record.birthday && dayjs(record.birthday).format('YYYY-MM-DD')) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>出生地：{record.birthPlace || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>性别：{(record.memGender !== null && (Number(record.memGender) === 1 ? '男' : '女')) || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>收款人姓名(英文)：{record.payeeUsername || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>收款人银行预留电话：{record.payeePhone || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>收款人境外汇款住址(英文)：{record.payeeAddress || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>开户行名称(英文)：{record.bankName || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>银行账号：{record.bankCardNo || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <span>SWIFT/BIC Code：{record.swiftBicCode || <Text type="secondary">&ndash;</Text>}</span>
                            <br />
                            <div>{record.laborInfoConfirmed ? <Text type="success">已确认</Text> : <Text type="secondary">未反馈</Text>}</div>
                        </>
                    )}
                </>
            ),
        },
        {
            title: '参会信息',
            dataIndex: 'signTime',
            width: 200,
            render: (_, record) => (
                <>
                    <div>签到时间：{record.signTime ? convertDateTimeToLocalText(record.signTime) : <Text type="secondary">暂无</Text>}</div>
                    <div>
                        签到方式：
                        {record.checkType === 1 && '线下核销签到'}
                        {record.checkType === 2 && '线上自助签到'}
                        {record.checkType === 3 && <Text type="danger">后台代签</Text>}
                        {!record.checkType && <Text type="secondary">暂无</Text>}
                    </div>
                    <div>
                        参会凭证：
                        {record.joinCert ? (
                            <Button type="link" size="small" style={{ paddingLeft: 0, paddingRight: 0 }} onClick={() => setPreviewImage(record.joinCert)}>
                                查看
                            </Button>
                        ) : (
                            <Text type="secondary">暂无</Text>
                        )}
                    </div>
                </>
            ),
        },
        {
            title: '付款信息',
            dataIndex: 'payedAt',
            width: 200,
            render: (_, record) => {
                // 从付款申请记录中找到当前批次的数据
                const payRecords = record.laborFeePayRecordDtoList || [];
                const currentBatchRecord = payRecords.find(payRecord => payRecord.batchNo === selectedBatchNo);

                if (!currentBatchRecord) {
                    return <Text type="secondary">暂无</Text>;
                }

                return (
                    <>
                        <div>
                            <Text>付款日期：</Text>
                            {currentBatchRecord.payedAt ? dateTimeFormat(currentBatchRecord.payedAt) : <Text type="secondary">暂无</Text>}
                        </div>
                        <div>
                            <Text>付款凭证：</Text>
                            {currentBatchRecord.paymentCert ? (
                                <Button type="link" size="small" style={{ paddingLeft: 0, paddingRight: 0 }} onClick={() => setPreviewImage(currentBatchRecord.paymentCert)}>
                                    查看
                                </Button>
                            ) : (
                                <Text type="secondary">暂无</Text>
                            )}
                        </div>
                        {currentBatchRecord.notes && (
                            <div>
                                <Alert
                                    title={
                                        <div>
                                            <span>{currentBatchRecord.notes}</span>
                                            <br />
                                            <span style={{ color: '#999' }}>{dayjs(currentBatchRecord.notesAt).format('YYYY年M月D日 HH:mm:ss')}</span>
                                        </div>
                                    }
                                    type="warning"
                                />
                            </div>
                        )}
                    </>
                );
            },
        },
    ];

    // 获取付款批次列表，返回最新列表数据
    const fetchBatchList = async () => {
        const res = await fetchLaborFeePaymentBatchesList({ projectCode: 'YHY', projectId: conferenceId, requestFrom: 1 }); // 1-业务模块  2-财务模块
        if (res.status_code === 200 && res.data) {
            const list = res.data || [];
            setBatchList(list);
            return list;
        }
        return [];
    };

    // 初始化时获取批次列表和会议列表
    useEffect(() => {
        if (isOpen && conferenceId) {
            fetchBatchList();
            getConferenceList();
        }
    }, [isOpen, conferenceId, getConferenceList]);

    // 更新列表
    useEffect(() => {
        if (filter?.batchNo) {
            fetchTableData();
        }
    }, [filter]);

    // 打开审批详情 - 修改为先检查未填写劳务费的专家
    const handleOpenApprovalDetails = async () => {
        // 先检查是否有未填写劳务费的专家
        try {
            const params = {
                projectCode: 'YHY',
                projectId: conferenceId,
                batchNo: selectedBatchNo,
                hasLaborFee: 0, // 0表示未填写
                page: 1,
                pageSize: 1000,
            };

            const res = await laborFeeListApi(params);
            if (res && res.status_code === 200) {
                const unFilledExperts = res.data?.list || [];

                if (unFilledExperts.length > 0) {
                    // 获取未填写劳务费的专家姓名
                    const expertNames = unFilledExperts.map(expert => expert.memUsername).join('、');

                    // 显示二次确认弹框
                    Modal.warning({
                        title: '提示',
                        content: (
                            <div>
                                以下专家未填写本次劳务费，请确认填写后再提交：
                                <br />
                                <strong>{expertNames}</strong>
                            </div>
                        ),
                        okText: '知道了',
                        onOk: () => {
                            // 用户点击知道了后，不执行打开审批详情操作
                        },
                        mask: { closable: true },
                        keyboard: true,
                    });

                    return; // 阻止后续打开审批详情流程
                }
            }
        } catch (error) {
            console.error('检查未填写劳务费专家失败:', error);
        }

        // 没有未填写的专家，或者检查过程中出现错误，继续打开审批详情
        setSubmitAuditDrawerOpen(true);
    };

    // 作废当前付款申请批次
    const handleInvalid = () => {
        Modal.confirm({
            title: '提示',
            content: '确认后将作废当前付款申请且不可恢复？',
            okText: '确认',
            cancelText: '取消',
            okButtonProps: { danger: true, loading: invalidLoading },
            onOk: async () => {
                setInvalidLoading(true);
                try {
                    const res = await laborFeeBatchInvalidApi({ batchNo: selectedBatchNo });
                    if (res && res.status_code === 200) {
                        message.success('作废成功');
                        // 通知父级刷新批次列表状态（"已创建付款申请"按钮显示判断）
                        onInvalidSuccess?.();
                        // 先清空当前选中批次
                        setSelectedBatchNo(null);
                        // 重新获取批次列表
                        const newList = await fetchBatchList();
                        if (newList.length > 0) {
                            // 有数据则选中第一条，右侧数据随之联动（useEffect 会自动触发）
                            const firstBatch = newList[0];
                            setSelectedBatchNo(firstBatch.batchNo);
                            setFilter(prev => ({ ...prev, page: 1, pageSize: 10, batchNo: firstBatch.batchNo }));
                            getBatchStatisticalAmount(firstBatch.batchNo);
                        } else {
                            // 列表为空，清空右侧数据并关闭弹窗，父级页面刷新
                            setTableData([]);
                            setTotal(0);
                            setBatchStatisticalAmount(null);
                            setFilter({ page: 1, pageSize: 10 });
                            filterForm.resetFields();
                            onClose();
                        }
                    }
                } catch (error) {
                    console.error('作废失败', error);
                    message.error(error.message || '作废失败');
                } finally {
                    setInvalidLoading(false);
                }
            },
        });
    };

    // 导出劳务费信息
    const exportLaborFee = () => {
        window.location.href = `${config.API_PREFIX}/user/laborAudit/pay-batch-infos-download-v2?batchNo=${selectedBatchNo}`;
    };

    // 处理批次选择（仅接收 batchNo 参数）
    const handleSelectBatch = useCallback(
        async batchNo => {
            if (!batchNo) return;

            // 更新选中的批次号
            setSelectedBatchNo(batchNo);

            // 更新筛选条件
            setFilter(prev => ({ ...prev, page: 1, pageSize: 10, batchNo }));

            // 请求批次统计信息
            getBatchStatisticalAmount(batchNo);
        },
        [conferenceId],
    );

    // 格式化货币
    const formatCurrency = value => {
        const f = parseFloat(value) || 0.0;
        const parts = f.toFixed(2).split('.');
        const result = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,') + (parts[1] ? `.${parts[1]}` : '');
        return result;
    };

    // 获取含税金额
    const getTotalAmount = (LaborFeeSum, IndividualTaxSum) => {
        if (LaborFeeSum !== null && IndividualTaxSum !== null) {
            return (Number(LaborFeeSum) + Number(IndividualTaxSum)).toFixed(2);
        }
    };

    // 筛选表单提交
    const onFormFinish = values => {
        // 只保存有效值
        const validValues = {};
        Object.keys(values).forEach(key => {
            const val = values[key];
            if (val !== undefined && val !== null && val !== '') {
                if (Array.isArray(val) && val.length === 0) return;
                validValues[key] = val;
            }
        });
        setFilter({ page: 1, pageSize: filter.pageSize, ...validValues, batchNo: filter.batchNo });
    };

    // 重置筛选
    const onReset = () => {
        filterForm.resetFields();
        setFilter({ page: 1, pageSize: 10, batchNo: filter.batchNo });
    };

    // 分页变化
    const onChangePage = (page, pageSize) => {
        setFilter(prev => ({ ...prev, page, pageSize }));
    };

    const getBatchStatusClassName = (approvalStatus, nodeId) => {
        switch (approvalStatus) {
            case 2:
                if (nodeId === 7 || nodeId === 9) {
                    return 'batch-status--warning';
                }
                return 'batch-status--success';
            case 1:
                return 'batch-status--warning';
            case 3:
                if (nodeId === 7 || nodeId === 9) {
                    return 'batch-status--success';
                }
                if (nodeId === 10) {
                    return 'batch-status--success';
                }
                return 'batch-status--error';
            case null:
                if (nodeId === -1) {
                    return 'batch-status--success';
                }
                return 'batch-status--default';
            default:
                return 'batch-status--warning';
        }
    };

    // 获取当前登录用户的手机号
    const getCurrentUserPhone = () => {
        const adminInfo = localStorage.getItem('infos');
        let userPhone = null;
        if (adminInfo && JSON.parse(adminInfo)) {
            const info = JSON.parse(adminInfo)?.data;
            userPhone = JSON.parse(info)?.phone;
        }
        return userPhone;
    };

    // 是否有权导出劳务费明细
    const canExportLaborFee = useMemo(() => {
        const roleList = localStorageGet('roleList') ? JSON.parse(localStorageGet('roleList')) : [];
        if (roleList?.length) {
            return roleList.some(item => item.name === '劳务费管理员' || item.name === '财务劳务费管理员' || item.name === '财务劳务费付款人员');
        }
        return false;
    }, []);

    // 获取当前选中批次的信息（直接根据 selectedBatchNo 查找）
    const currentBatch = useMemo(() => {
        if (!selectedBatchNo) return null;
        return batchList.find(batch => batch.batchNo === selectedBatchNo);
    }, [batchList, selectedBatchNo]);

    // 判断是否应该显示"提交申请"按钮
    const shouldShowSubmitButton = useMemo(() => {
        // 只有当选中了批次且该批次的nodeId不存在时 或 该批次nodeId为1，且approvalStatus为1时，才显示提交申请按钮
        if (!currentBatch) return false;
        return !currentBatch?.nodeId || (currentBatch?.nodeId === 1 && currentBatch?.approvalStatus === 1);
    }, [currentBatch]);

    // 判断是否应该显示"作废"按钮：在提交申请条件基础上，额外要求当前登录人与当前节点经办人一致
    const shouldShowInvalidButton = useMemo(() => {
        if (!shouldShowSubmitButton) return false;
        const userPhone = getCurrentUserPhone();
        // 批次尚未提交过（approvalBy 为空）时，默认允许作废；否则需要与经办人手机号一致
        if (!currentBatch?.approvalBy) return true;
        return currentBatch?.approvalBy === userPhone;
    }, [shouldShowSubmitButton, currentBatch]);

    // 当前登录用户与当前待审核节点的审批人一致时，显示审批按钮（排除经办人待审核）
    const shouldShowApprovalButton = useMemo(() => {
        const userPhone = getCurrentUserPhone();
        return currentBatch?.approvalBy === userPhone && currentBatch?.nodeId !== 1;
    }, [currentBatch]);

    return (
        <>
            <Drawer
                title={<div style={{ textAlign: 'center', width: '100%' }}>付款申请批次</div>}
                placement="bottom"
                onClose={onClose}
                open={isOpen}
                size="100vh"
                styles={{
                    body: { padding: 0, height: '100%' },
                    wrapper: { height: '100vh' },
                }}
                closable={{ placement: 'end' }}
            >
                <div className="payment-batch-drawer">
                    {/* 左侧：付款批次列表 */}
                    <div className="batch-list-panel">
                        <div className="batch-list-content">
                            {batchList.length > 0 ? (
                                <div className="batch-items">
                                    {batchList.map(batch => {
                                        // 获取状态文本和颜色
                                        const statusClassName = getBatchStatusClassName(batch.approvalStatus, batch.nodeId);
                                        const formattedDate = batch.batchCreatedAt || <Text type="secondary">&ndash;</Text>;

                                        // 构建第二行左侧显示内容：nodeName{(approvalByName)} 或 approvalByName
                                        const getNodeDisplay = () => {
                                            const nodeName = batch.nodeName || '';
                                            const approvalByName = batch.approvalByName || '';

                                            if (nodeName && approvalByName) {
                                                return `${nodeName}（${approvalByName}）`;
                                            } else if (nodeName) {
                                                return nodeName;
                                            }
                                            return '';
                                        };

                                        return (
                                            <div key={batch.batchNo} className={`batch-item ${selectedBatchNo === batch.batchNo ? 'batch-item-selected' : ''}`} onClick={() => handleSelectBatch(batch.batchNo)}>
                                                <div className="batch-date">付款申请批次：{formattedDate}</div>
                                                <div className={`batch-status ${statusClassName}`}>
                                                    <span>{getNodeDisplay()}</span>
                                                    <span>{batch.approvalStatusName}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="empty-state">暂无付款批次</div>
                            )}
                        </div>
                    </div>

                    {/* 右侧：筛选和表格 */}
                    <div className="batch-detail-panel">
                        <div className="filter-section">
                            {/* 筛选表单 */}
                            <Form form={filterForm} name="control-hooks" {...filterFormItemLayout} onFinish={onFormFinish}>
                                <Row>
                                    <Col {...filterFormGridLayout}>
                                        <Form.Item name="memUsername" label="姓名">
                                            <Input placeholder="请输入专家姓名" allowClear />
                                        </Form.Item>
                                    </Col>
                                    <Col {...filterFormGridLayout}>
                                        <Form.Item name="memMobile" label="手机号">
                                            <Input placeholder="请输入手机号" allowClear />
                                        </Form.Item>
                                    </Col>

                                    <Col {...filterFormGridLayout}>
                                        <Form.Item label="专家级别" name="expertLevels">
                                            <Select mode="multiple" maxTagCount="responsive" options={expertLevels} fieldNames={{ label: 'label', value: 'level' }} placeholder="请选择专家级别" allowClear />
                                        </Form.Item>
                                    </Col>

                                    <Col {...filterFormGridLayout}>
                                        <Form.Item name="conferenceHallId" label="任务所属会议">
                                            <TreeSelect showSearch={{ filterTreeNode: filterTreeNodeByTitle }} styles={{ popup: { root: { minWidth: 500, overflow: 'auto' } } }} allowClear treeDefaultExpandAll placement="bottomRight" treeData={conferenceHallTreeData} placeholder="请选择会议" />
                                        </Form.Item>
                                    </Col>

                                    <Col {...filterFormGridLayout}>
                                        <Form.Item name="hasLaborFee" label="劳务费填写">
                                            <Select placeholder="请选择填写情况" allowClear>
                                                <Select.Option value={1}>已填写</Select.Option>
                                                <Select.Option value={0}>未填写</Select.Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>

                                    <Col {...filterFormGridLayout}>
                                        <Form.Item name="laborNeed" label="劳务需求">
                                            <Select placeholder="请选择" allowClear>
                                                <Select.Option value={1}>需要</Select.Option>
                                                <Select.Option value={2}>不需要</Select.Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>

                                    <Col {...filterFormGridLayout}>
                                        <Form.Item name="laborPayStatus" label="付款状态">
                                            <Select placeholder="请选择" allowClear>
                                                <Select.Option value={0}>待付款</Select.Option>
                                                <Select.Option value={1}>已付款</Select.Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>

                                    <Col {...filterFormGridLayout}>
                                        <Form.Item name="costBearer" label="支付主体">
                                            <Select placeholder="请选择" allowClear options={laborFeeCostBearers} fieldNames={{ label: 'label', value: 'value' }} />
                                        </Form.Item>
                                    </Col>

                                    <Col className="filter-form-buttons" {...filterFormGridLayout}>
                                        <Space>
                                            <Button type="primary" htmlType="submit">
                                                检索
                                            </Button>
                                            <Button type="default" onClick={onReset}>
                                                重置
                                            </Button>
                                        </Space>
                                    </Col>
                                </Row>
                            </Form>

                            {/* 操作按钮 */}
                            <div className="action-toolbar">
                                <Space>
                                    {shouldShowInvalidButton && (
                                        <Button danger loading={invalidLoading} onClick={handleInvalid}>
                                            作废
                                        </Button>
                                    )}

                                    {shouldShowSubmitButton && (
                                        <Button type="primary" onClick={handleOpenApprovalDetails}>
                                            提交申请
                                        </Button>
                                    )}

                                    {shouldShowApprovalButton && (
                                        <Button type="primary" onClick={handleOpenApprovalDetails}>
                                            去审批
                                        </Button>
                                    )}

                                    {/* 当前批次的经办人提交状态是已提交时展示该按钮 待提交时 - nodeId不存在*/}
                                    {currentBatch?.nodeId && <Button onClick={handleOpenApprovalDetails}>审批详情</Button>}

                                    {canExportLaborFee && currentBatch && (
                                        <Tooltip title="导出劳务费明细">
                                            <Button
                                                icon={
                                                    <span className="anticon">
                                                        <i className="bi bi-cloud-download"></i>
                                                    </span>
                                                }
                                                onClick={exportLaborFee}
                                            />
                                        </Tooltip>
                                    )}
                                </Space>
                            </div>

                            <Alert
                                type="info"
                                style={{ marginBottom: 5, fontSize: 12 }}
                                title={
                                    <>
                                        <div style={{ marginBottom: 5 }}>
                                            <div>
                                                <span style={{ fontWeight: 600 }}>项目信息：</span>
                                                <span>
                                                    {conference?.name}，氚云项目编号：{conference?.h3yunNo}，会议项目编号：{numberProject('YHY', conference?.id)}
                                                </span>
                                            </div>
                                        </div>

                                        {/* 本批次付款申请 */}
                                        {batchStatisticalAmount && (
                                            <div style={{ marginBottom: 5 }}>
                                                <div>
                                                    <span style={{ fontWeight: 600 }}>本批次付款申请：</span>
                                                    <span>
                                                        专家总数：{batchStatisticalAmount?.expertSum || 0}人，需要劳务费人数：{batchStatisticalAmount?.realTotal || 0}人，付款申请人数：{batchStatisticalAmount?.expertAppliedSum || 0}人，已付款人数：{batchStatisticalAmount?.expertPaySum || 0}人，实发劳务费合计金额（元）：
                                                        {formatCurrency(batchStatisticalAmount?.LaborFeeSum || 0)}，个人所得税合计金额（元）：{formatCurrency(batchStatisticalAmount?.IndividualTaxSum || 0)}，含税劳务费合计金额（元）：
                                                        {formatCurrency(getTotalAmount(batchStatisticalAmount?.LaborFeeSum, batchStatisticalAmount?.IndividualTaxSum))}
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {/* 本项目累计付款申请（含本批次）*/}
                                        {statisticalAmount && (
                                            <div style={{ marginBottom: 5 }}>
                                                <div>
                                                    <span style={{ fontWeight: 600 }}>本项目累计付款申请（含本批次）：</span>
                                                    <span>
                                                        专家总数：{statisticalAmount?.expertSum || 0}人，需要劳务费人数：{statisticalAmount?.realTotal || 0}人，付款申请人数：{statisticalAmount?.expertAppliedSum || 0}人，已付款人数：{statisticalAmount?.expertPaySum || 0}人，实发劳务费合计金额（元）：
                                                        {formatCurrency(statisticalAmount?.LaborFeeSum || 0)}，个人所得税合计金额（元）：{formatCurrency(statisticalAmount?.IndividualTaxSum || 0)}，含税劳务费合计金额（元）：
                                                        {formatCurrency(getTotalAmount(statisticalAmount?.LaborFeeSum, statisticalAmount?.IndividualTaxSum))}
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {/* 劳务需求备注 */}
                                        <div>
                                            <span style={{ fontWeight: 600 }}>劳务需求备注：</span>
                                            <span>{conference?.laborFeeDemandRemarks || '未填写'}</span>
                                        </div>
                                    </>
                                }
                            />
                        </div>

                        {/* 表格内容 */}
                        <div className="table-section">
                            <Table
                                loading={exportLoading}
                                columns={batchDefaultColumns}
                                dataSource={tableData}
                                rowKey="expertId"
                                scroll={{ x: 1500 }}
                                pagination={{
                                    total: total,
                                    showTotal: () => `共${total}条`,
                                    current: filter?.page || 1,
                                    pageSize: filter?.pageSize || 10,
                                    showQuickJumper: true,
                                    showSizeChanger: true,
                                    onChange: onChangePage,
                                    pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
                                    onShowSizeChange: (_, pageSize) => onChangePage(1, pageSize),
                                }}
                            />
                        </div>
                    </div>
                </div>
            </Drawer>
            {/* SubmitAuditDrawer 组件 */}
            <SubmitAuditDrawer
                open={submitAuditDrawerOpen}
                onClose={() => setSubmitAuditDrawerOpen(false)}
                conferenceId={conferenceId}
                conference={conference}
                currentBatch={currentBatch}
                projectId={conferenceId}
                batchNo={selectedBatchNo}
                onApprovalSuccess={(options = {}) => {
                    // 刷新付款批次列表
                    fetchBatchList();
                    // 作废操作时，重置选中的批次，自动选中列表第一个批次
                    if (options.shouldResetBatch) {
                        setSelectedBatchNo(null);
                    }
                    onApprovalFlowComplete?.();
                }}
            />
            {/* 编辑弹框 */}
            <Modal
                title="编辑劳务费信息"
                open={editModalOpen}
                onCancel={handleCloseEditModal}
                centered
                width={700}
                footer={[
                    <Button key="cancel" onClick={handleCloseEditModal}>
                        取消
                    </Button>,
                    <Button key="submit" type="primary" onClick={handleSubmitEditForm}>
                        保存
                    </Button>,
                ]}
            >
                {selectedRecord && (
                    <div className="edit-modal-content">
                        {/* 顶部专家基本信息卡片 */}
                        {(() => {
                            // 获取专家等级信息
                            const levelInfo = expertLevels.find(a => a.level === selectedRecord.expertLevel);
                            return (
                                <div className="expert-info-card">
                                    <div className="info-item">
                                        <span className="label">姓名：</span>
                                        <span className="value">{selectedRecord.memUsername || '–'}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="label">级别：</span>
                                        <span className="value line-level">{levelInfo ? levelInfo.label : '–'}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="label">单位：</span>
                                        <span className="value">{selectedRecord.workExpList && selectedRecord.workExpList.length > 0 ? selectedRecord.workExpList[0].unitName : '–'}</span>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* 付款申请记录卡片 */}
                        {(() => {
                            // 过滤掉当前选中的批次
                            const filteredPayRecords = selectedRecord.laborFeePayRecordDtoList?.filter(payRecord => payRecord.batchNo !== selectedBatchNo) || [];
                            return (
                                filteredPayRecords.length > 0 && (
                                    <div className="payment-records-section">
                                        <div className="section-title">其他批次付款申请记录</div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            {filteredPayRecords.map(payRecord => {
                                                const costBearerLabel = laborFeeCostBearers.find(b => b.value === payRecord.costBearer)?.label || <Text type="secondary">暂无</Text>;

                                                // 判断是否为外币
                                                const isForeignCurrency = payRecord.currency && payRecord.currency !== 'CNY';
                                                const currencyType = CurrencyList.find(c => c.currency === payRecord.currency)?.name || payRecord.currency;

                                                return (
                                                    <div key={payRecord.batchNo} className="pay-record-card">
                                                        <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
                                                            <div>
                                                                申请批次：{convertDateTimeToLocalText(payRecord.createdAt)}
                                                                {payRecord.createdName && `(${payRecord.createdName})`}
                                                            </div>
                                                            <div>支付主体：{costBearerLabel}</div>
                                                            {isForeignCurrency ? (
                                                                // 外币展示格式
                                                                <>
                                                                    <div>
                                                                        外币实发额({currencyType})：{payRecord.foreignExchangeAmount !== null ? formatCurrency(payRecord.foreignExchangeAmount) : <Text type="secondary">暂无</Text>}(
                                                                        {payRecord.approvalStatus === 1 || !payRecord.approvalStatus ? <Text type="warning">待提交</Text> : payRecord.payedAt ? <Text type="success">已付款</Text> : <Text type="warning">待付款</Text>})
                                                                    </div>
                                                                    <div>折合人民币实发劳务费(元)：{payRecord.laborFeeAmount !== null ? `${formatCurrency(payRecord.laborFeeAmount)}` : <Text type="secondary">暂无</Text>}</div>
                                                                </>
                                                            ) : (
                                                                // 人民币展示格式
                                                                <div>
                                                                    实发劳务费(元)：{payRecord.laborFeeAmount !== null ? `${formatCurrency(payRecord.laborFeeAmount)}` : <Text type="secondary">暂无</Text>}(
                                                                    {payRecord.approvalStatus === 1 || !payRecord.approvalStatus ? <Text type="warning">待提交</Text> : payRecord.payedAt ? <Text type="success">已付款</Text> : <Text type="warning">待付款</Text>})
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )
                            );
                        })()}

                        {/* 编辑表单 */}
                        <Form form={editForm} style={{ marginTop: 20 }} labelCol={{ span: 7 }} wrapperCol={{ span: 17 }}>
                            {/* 付款方式选择 */}
                            <Form.Item label="付款方式" name="paymentMethod" rules={[{ required: true, message: '请选择付款方式' }]}>
                                <Radio.Group onChange={e => setSelectedPaymentMethod(e.target.value)}>
                                    <Radio value={1}>银行转账</Radio>
                                    <Radio value={2}>现金支付</Radio>
                                    <Radio value={3}>无需支付</Radio>
                                </Radio.Group>
                            </Form.Item>

                            {/* 根据付款方式动态展示字段 */}
                            {selectedPaymentMethod !== 3
                                && (selectedCurrency === 'CNY' ? (
                                    // 人民币场景
                                    <>
                                        <Form.Item label="实际支付货币" name="currency" rules={[{ required: true, message: '请选择实际支付货币' }]}>
                                            <Select options={CurrencyList.map(c => ({ label: c.name, value: c.currency }))} onChange={value => setSelectedCurrency(value)} />
                                        </Form.Item>

                                        <Form.Item label="支付主体" name="costBearer" rules={[{ required: true, message: '请选择支付主体' }]}>
                                            <Select options={laborFeeCostBearers} fieldNames={{ label: 'label', value: 'value' }} placeholder="请选择支付主体" />
                                        </Form.Item>

                                        <Form.Item label="实发劳务费(元)" name="laborFeeAmount" rules={[{ required: true, message: '请输入实发劳务费' }]}>
                                            <InputNumber placeholder="请输入金额" min={0} precision={2} style={{ width: '100%' }} onBlur={handleForeignAmountBlur} />
                                        </Form.Item>

                                        <Form.Item label="个人所得税(元)">
                                            <div style={{ lineHeight: '32px' }}>{editingRecord?.individualTaxAmount ? <>&yen;{formatCurrency(editingRecord?.individualTaxAmount)}</> : <Text type="secondary">&ndash;</Text>}</div>
                                        </Form.Item>

                                        <Form.Item label="含税劳务费(元)">
                                            <div style={{ lineHeight: '32px' }}>{editingRecord?.laborFeeAmount && editingRecord?.individualTaxAmount ? <>&yen;{formatCurrency(parseFloat(editingRecord?.laborFeeAmount) + parseFloat(editingRecord?.individualTaxAmount))}</> : <Text type="secondary">&ndash;</Text>}</div>
                                        </Form.Item>
                                    </>
                                ) : (
                                    // 非人民币（外币）场景
                                    <>
                                        <Form.Item label="实际支付货币" name="currency" rules={[{ required: true, message: '请选择实际支付货币' }]}>
                                            <Select options={CurrencyList.map(c => ({ label: c.name, value: c.currency }))} onChange={value => setSelectedCurrency(value)} />
                                        </Form.Item>

                                        <Form.Item label="支付主体">
                                            <div style={{ lineHeight: '32px', fontSize: 14 }}>健康界科技</div>
                                        </Form.Item>

                                        <Form.Item name="foreignExchangeAmount" label={`外币实发额(${CurrencyList.find(c => c.currency === selectedCurrency)?.name || selectedCurrency})`} rules={[{ required: true, message: '请输入外币实发额' }]}>
                                            <InputNumber placeholder="请输入金额" min={0} precision={2} style={{ width: '100%' }} onBlur={handleForeignAmountBlur} />
                                        </Form.Item>

                                        <Form.Item label="折合人民币实发劳务费(元)">
                                            <Spin spinning={taxCalculating}>
                                                <div style={{ lineHeight: '32px' }}>{editingRecord?.cnyCurrencyLaborFeeAmount ? formatCurrency(editingRecord?.cnyCurrencyLaborFeeAmount) : <Text type="secondary">&ndash;</Text>}</div>
                                            </Spin>
                                        </Form.Item>

                                        <Form.Item label="折合人民币个人所得税(元)">
                                            <Spin spinning={taxCalculating}>
                                                <div style={{ lineHeight: '32px' }}>{editingRecord?.cnyCurrencyTaxAmount ? formatCurrency(editingRecord?.cnyCurrencyTaxAmount) : <Text type="secondary">&ndash;</Text>}</div>
                                            </Spin>
                                        </Form.Item>

                                        <Form.Item label="折合人民币含税劳务费(元)">
                                            <Spin spinning={taxCalculating}>
                                                <div style={{ lineHeight: '32px' }}>{editingRecord?.cnyCurrencyTotalAmount ? formatCurrency(editingRecord?.cnyCurrencyTotalAmount) : <Text type="secondary">&ndash;</Text>}</div>
                                            </Spin>
                                        </Form.Item>

                                        <Form.Item label="专家参与情况图片盖章版" name="expertParticipationPic" rules={[{ required: true, message: '请上传专家参与情况图片盖章版' }]}>
                                            {conferencePhotoUrl && (
                                                <div style={{ marginBottom: 8 }}>
                                                    <Image width={100} height={100} src={prepareBCOSSUrl(conferencePhotoUrl)} alt="专家参与情况图片盖章版" />
                                                    <Button
                                                        type="link"
                                                        size="small"
                                                        onClick={() => {
                                                            setConferencePhotoUrl('');
                                                            // 删除图片时手动触发校验
                                                            editForm.setFields([
                                                                {
                                                                    name: 'expertParticipationPic',
                                                                    touched: true,
                                                                    validating: false,
                                                                },
                                                            ]);
                                                        }}
                                                        style={{ marginLeft: 8 }}
                                                    >
                                                        删除
                                                    </Button>
                                                </div>
                                            )}
                                            <Upload beforeUpload={file => checkImgUploadFile(file, 5)} fileList={[]} customRequest={fileInfo => uploadImageFile(fileInfo, 'expertParticipationPic')}>
                                                <Button type="primary" loading={conferencePhotoUploadLoading}>
                                                    {conferencePhotoUrl ? '更改照片' : '上传照片'}
                                                </Button>
                                            </Upload>
                                        </Form.Item>

                                        <Form.Item label="专家劳务确认函盖章版" name="expertLaborFeeConfirmPic" rules={[{ required: true, message: '请上传专家劳务确认函盖章版' }]}>
                                            {certificateFileUrl && (
                                                <div style={{ marginBottom: 8 }}>
                                                    <Image width={100} height={100} src={prepareBCOSSUrl(certificateFileUrl)} alt="专家劳务确认函盖章版" />
                                                    <Button
                                                        type="link"
                                                        size="small"
                                                        onClick={() => {
                                                            setCertificateFileUrl('');
                                                            // 删除图片时手动触发校验
                                                            editForm.setFields([
                                                                {
                                                                    name: 'expertLaborFeeConfirmPic',
                                                                    touched: true,
                                                                    validating: false,
                                                                },
                                                            ]);
                                                        }}
                                                        style={{ marginLeft: 8 }}
                                                    >
                                                        删除
                                                    </Button>
                                                </div>
                                            )}
                                            <Upload beforeUpload={file => checkImgUploadFile(file, 5)} fileList={[]} customRequest={fileInfo => uploadImageFile(fileInfo, 'expertLaborFeeConfirmPic')}>
                                                <Button type="primary" loading={certificateFileUploadLoading}>
                                                    {certificateFileUrl ? '更改照片' : '上传照片'}
                                                </Button>
                                            </Upload>
                                        </Form.Item>
                                    </>
                                ))}
                        </Form>
                    </div>
                )}
            </Modal>
            {/* 专家参会照片预览 Modal */}
            <Modal title="专家参会照片盖章版" open={!!previewImage} onCancel={() => setPreviewImage(null)} footer={null} centered width="auto" styles={{ body: { padding: 0, textAlign: 'center', background: '#f0f0f0' } }} destroyOnHidden>
                {previewImage && <Image src={prepareBCOSSUrl(previewImage)} style={{ maxWidth: '90vw', maxHeight: '90vh' }} preview={false} alt="专家参会照片盖章版" />}
            </Modal>
        </>
    );
};

export default PaymentBatchDrawer;
